/**
 * The way in.
 *
 * The app used to open straight onto the paper, and everything else lay
 * behind the burger menu: whoever opened it could not see that seven words
 * were waiting, and the exercises — the point of the whole thing — were the
 * hardest part to reach.
 *
 * Three ways, in the order in which they are meant: writing, practising,
 * photographing. Below them the figures, and they lie loose on the ground
 * rather than on a card of their own: they are information at the edge, not a
 * fourth thing to operate.
 */

import type { ComponentChildren } from 'preact'
import { useSignal } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import * as st from '../state.js'
import { Icon } from './icons.js'
import { lastDays } from '../exercise/time.js'

/** The weekday letters, Monday first — `getDay()` counts from Sunday. */
const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

export function Start({
  onWrite, onPractise, onPhoto,
}: {
  onWrite: () => void
  onPractise: () => void
  onPhoto: () => void
}) {
  /**
   * The week, read once on opening.
   *
   * Not on every render: the row is drawn from `localStorage`, and reading it
   * inside the render would make every keystroke elsewhere touch the disk.
   */
  const week = useSignal(lastDays(7))

  useEffect(() => {
    week.value = lastDays(7)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const days = week.value
  // A day with a photograph counts too: nothing was timed, but a page was
  // brought in, and „5 von 7 Tagen" is about showing up.
  const practised = days.filter((d) => d.ms > 0 || d.photos > 0).length
  const minutes = Math.round(days.reduce((n, d) => n + d.ms, 0) / 60000)
  /** The tallest bar sets the scale — an hour and a five-minute day differ. */
  const top = Math.max(...days.map((d) => d.ms), 1)
  const open = st.openIssues.value
  const written = Math.round(days.reduce((n, d) => n + d.write, 0) / 60000)
  const drilled = Math.round(days.reduce((n, d) => n + d.drill, 0) / 60000)
  const fotos = days.reduce((n, d) => n + d.photos, 0)
  /* Writing leads on a machine with a keyboard and steps back on a touch
     device — see `canWrite` in state.ts. It is never hidden: the child
     should see that the app can do it, and where. */
  const writeFirst = st.canWrite.value
  /** A parent looks and photographs; writing is the child's. */
  const parent = st.isParent()

  return (
    <div class="start">
      {/* No mark here. It was tried beside the greeting and measured: at the
          40px that fit next to the line, the scribble's strokes merge into a
          hatched block — it only reads as a scribble from about 48px up, and
          at that size it stands 1.5 times taller than the name beside it.
          
          The greeting is what this line is for. The mark has the sign-in
          screen, where it has both room and a reason.

          The name comes from the server, not from the code: it stood here as
          "Elmar" and greeted everybody by that name, although the database
          has a `users.name` column precisely so it does not have to. */}
      <p class="start-hello">{greeting(st.user.value?.name)}</p>

      {/* A parent is told whose Kritzl this is, and that nothing done here
          counts — once, at the top, where the greeting already made the
          first half of that point. */}
      {parent && (
        <p class="start-parent">
          Du siehst {possessive(st.user.value?.name)} Kritzl. Was du hier übst, zählt nicht für
          {' '}{st.user.value?.name || 'das Kind'}.
        </p>
      )}

      {/* On a machine with a keyboard, writing comes first — it is the main
          form of practising, not a side path. The tile carries its own
          colour for that: warm where the rest of the app is cool, the same
          one the figures use for written minutes. */}
      {writeFirst && !parent && <WriteTile onWrite={onWrite} />}

      <div class="start-tiles">
        <button type="button" class="start-tile primary" onClick={onPractise}>
          <Icon name="check" size={26} />
          <span class="start-tile-grow" />
          <span class="start-tile-title">Wiederholen</span>
          <span class="start-tile-hint">
            {open > 0
              ? `${open} ${open === 1 ? 'Wort wartet' : 'Wörter warten'}`
              : 'Aufgaben für dich'}
          </span>
        </button>

        <button type="button" class="start-tile start-tile-photo" onClick={onPhoto}>
          <Icon name="camera" size={26} />
          <span class="start-tile-grow" />
          <span class="start-tile-title">Foto</span>
          <span class="start-tile-hint">Heft abfotografieren</span>
        </button>
      </div>

      {/* On a touch device writing steps down here: not the main way, but
          visible — and the line says where it belongs. */}
      {!writeFirst && !parent && <WriteHint onWrite={onWrite} />}

      {/* The week.

          Two colours now, and only two: warm for writing, blue for the
          exercises. Not the chart palette — that one means KIND OF MISTAKE,
          and the same blue would mean a weekday here.

          Writing sits at the bottom of the bar, because it is the base: the
          drilling is what is added on top of it.

          A day without practice keeps a flat trace instead of disappearing —
          the gap is what the row is there to show. */}
      {/* The whole card leads on, not a link in its corner: everything on it
          is about the same thing, and a card one can read is a card one will
          tap. The chevron stays as the sign that it does. */}
      <button type="button" class="start-week" onClick={() => st.goTo('stats')}>
        <div class="start-week-top">
          <p class="start-week-head">
            {practised > 0
              ? `${practised} von 7 Tagen geübt`
              : 'Diese Woche noch nicht geübt'}
          </p>
          <span class="start-more">
            Mehr
            <Icon name="chevron-right" size={16} />
          </span>
        </div>

        {/* The legend ABOVE the bars, not below them.
            
            It says what the colours mean, so it belongs before the thing it
            explains — and it leaves the row of bars to close the card. The
            page then ends on their baseline instead of on a list of text. */}
        <div class="start-week-legend">
          {minutes > 0 || fotos > 0
            ? weekLegend(written, drilled, fotos)
            : <span class="start-week-none">Noch nichts diese Woche</span>}
        </div>

        <div class="start-week-row">
          {days.map((d, i) => {
            const isToday = i === days.length - 1
            const label = WEEKDAYS[dayIndex(d.day)] ?? ''
            const full = d.ms > 0 ? Math.max(8, Math.round((d.ms / top) * 52)) : 0
            // The two pieces in proportion, but each at least 3px when it
            // has any time at all: a minute of writing next to an hour of
            // drilling would otherwise round away to nothing.
            const wPart = d.write > 0 ? Math.max(3, Math.round((d.write / d.ms) * full)) : 0
            const dPart = Math.max(0, full - wPart)
            // At most five strokes: a sixth would push the bar up without
            // saying more than „a lot".
            const strokes = Math.min(d.photos, 5)
            // The strokes live INSIDE the bar, so they have to be paid for in
            // height — otherwise a photographed day would look like less time
            // than it was. 2px each, 1px between them, 2px of ground on each
            // side; keep this in step with `.start-bar-photos`.
            const sPart = strokes > 0 ? strokes * 2 + (strokes - 1) + 4 : 0
            return (
              <div key={d.day} class="start-day">
                <div
                  class={`start-bar${d.ms > 0 || strokes > 0 ? '' : ' empty'}${isToday ? ' today' : ''}`}
                  style={d.ms > 0 || strokes > 0 ? { height: `${full + sPart}px` } : undefined}
                  /* The bar is a picture; the title says the same in words,
                     so neither height nor colour is the only carrier. */
                  title={dayTitle(label, d.write, d.drill, d.photos)}
                >
                  {d.drill > 0 && <span class="start-bar-drill" style={{ height: `${dPart}px` }} />}
                  {/* Between the two measured pieces, not outside them: the
                      bar keeps ONE rounded outline, and a photograph — which
                      has no duration — cannot pretend to be its top or its
                      base. Red below, teal in the middle, blue on top. */}
                  {strokes > 0 && (
                    <span class="start-bar-photos">
                      {Array.from({ length: strokes }, (_, k) => (
                        <span key={k} />
                      ))}
                    </span>
                  )}
                  {d.write > 0 && <span class="start-bar-write" style={{ height: `${wPart}px` }} />}
                </div>
                <span class={`start-day-label${isToday ? ' today' : ''}`}>{label}</span>
              </div>
            )
          })}
        </div>
      </button>
    </div>
  )
}

/**
 * The writing tile.
 *
 * Its own colour, warm against the cool of everything else: writing is the
 * main way of practising here, and a tile in the same blue as the exercises
 * would read as a lesser version of them.
 */
function WriteTile({ onWrite }: { onWrite: () => void }) {
  return (
    <button type="button" class="start-write" onClick={onWrite}>
      <span class="start-paper" aria-hidden="true" />
      <span class="start-write-body">
        <span class="start-write-text">
          <span class="start-write-title">Schreiben</span>
          <span class="start-write-hint">Das übt am meisten</span>
        </span>
        <span class="start-write-go">
          <Icon name="pencil" size={19} />
        </span>
      </span>
    </button>
  )
}

/**
 * The same tile on a touch device: below the others, and saying why.
 *
 * Not removed. Whoever cannot find writing on the phone would look for it —
 * the line says it exists and where it lives.
 */
function WriteHint({ onWrite }: { onWrite: () => void }) {
  return (
    <button type="button" class="start-write-small" onClick={onWrite}>
      <Icon name="pencil" size={18} />
      <span class="start-write-small-text">
        <span class="start-write-small-title">Schreiben</span>
        <span class="start-write-small-hint">Am besten am großen Bildschirm</span>
      </span>
      <Icon name="chevron-right" size={16} />
    </button>
  )
}

/** What the tooltip on a day says — the same as the colours, in words. */
function dayTitle(label: string, write: number, drill: number, photos = 0): string {
  const w = Math.round(write / 60000)
  const d = Math.round(drill / 60000)
  const parts: string[] = []
  if (w) parts.push(`${w} Min. geschrieben`)
  if (d) parts.push(`${d} Min. wiederholt`)
  if (photos) parts.push(`${photos} ${photos === 1 ? 'Seite' : 'Seiten'} fotografiert`)
  if (!parts.length) return `${label}: nichts gemacht`
  return `${label}: ${parts.join(', ')}`
}

/**
 * The line under the week — and the legend for the bars above it.
 *
 * The three figures were already named here, separated by dots. Putting a
 * chip of the matching colour in front of each one costs a line of nothing
 * and explains the bars without a legend of its own: whoever reads „26 Min.
 * geschrieben" beside a terracotta chip has learnt what the terracotta in
 * the bars means.
 *
 * Only the kinds that happened appear, so the legend never explains a colour
 * that is not on the screen.
 */
function weekLegend(written: number, drilled: number, photos = 0): ComponentChildren {
  const parts: { kind: string; text: string }[] = []
  if (written) parts.push({ kind: 'write', text: `${written} Min. geschrieben` })
  if (drilled) parts.push({ kind: 'drill', text: `${drilled} Min. wiederholt` })
  // Counted, not timed — a photograph has no duration.
  if (photos) {
    parts.push({
      kind: 'photo',
      text: `${photos} ${photos === 1 ? 'Seite' : 'Seiten'} fotografiert`,
    })
  }
  return parts.map((p) => (
    <span key={p.kind} class="start-week-part">
      <span class={`start-week-chip ${p.kind}`} />
      {p.text}
    </span>
  ))
}

/**
 * The greeting.
 *
 * Without a name just "Hallo": a user whose row carries no name is greeted
 * all the same, only not by somebody else's.
 */
function greeting(name?: string): string {
  const n = name?.trim()
  const view = st.user.value
  // The parent by their own name, the child's Kritzl named in the line
  // below — „Hallo Elmar" to Felix would be the wrong greeting twice over.
  if (view?.parent) return view.viewer ? `Hallo ${view.viewer}` : 'Hallo'
  return n ? `Hallo ${n}` : 'Hallo'
}

/** „Elmars", „Felix'" — the German possessive of a first name. */
function possessive(name: string | undefined): string {
  const n = name?.trim()
  if (!n) return 'dieses'
  return /[sßxz]$/i.test(n) ? `${n}’` : `${n}s`
}

/**
 * Which weekday a `YYYY-MM-DD` key is.
 *
 * Built by hand instead of `new Date(key)`: that would read the string as UTC
 * and put the day one back for anybody east of Greenwich — the row would be
 * labelled wrongly all afternoon.
 */
function dayIndex(key: string): number {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).getDay()
}
